import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { ArrowLeft, MessageSquare, CheckCircle, XCircle, MapPin, Clock, Send, AlertCircle, Loader2, List, Map as MapIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { VergeCondition, ReportComment } from '../types';
import { formatRelativeTime } from '../utils/dateFormat';

// Fix for default marker icons in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface AuthorityDashboardProps {
  onBack: () => void;
  localAuthority: string;
  userProfileId: string;
}

const gradeColors = {
  A: 'bg-green-500',
  B: 'bg-yellow-500',
  C: 'bg-orange-500',
  D: 'bg-red-500'
};

export function AuthorityDashboard({ onBack, localAuthority, userProfileId }: AuthorityDashboardProps) {
  const [reports, setReports] = useState<VergeCondition[]>([]);
  const [selectedReport, setSelectedReport] = useState<VergeCondition | null>(null);
  const [comments, setComments] = useState<ReportComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'closed'>('open');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  useEffect(() => {
    fetchReports();
  }, [localAuthority, filterStatus]);

  useEffect(() => {
    if (selectedReport) {
      fetchComments(selectedReport.id);
    }
  }, [selectedReport]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('verge_conditions')
        .select('*')
        .eq('local_authority', localAuthority)
        .order('timestamp', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (reportId: string) => {
    try {
      const { data, error } = await supabase
        .from('report_comments')
        .select(`
          *,
          profiles!report_comments_user_id_fkey (email)
        `)
        .eq('report_id', reportId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const commentsWithEmail = (data || []).map(comment => ({
        ...comment,
        user_email: comment.profiles?.email
      }));

      setComments(commentsWithEmail);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedReport) return;

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('report_comments')
        .insert([{
          report_id: selectedReport.id,
          user_id: userProfileId,
          comment: newComment.trim()
        }]);

      if (error) throw error;

      setNewComment('');
      await fetchComments(selectedReport.id);
      await fetchReports();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (report: VergeCondition) => {
    try {
      const newStatus = report.status === 'open' ? 'closed' : 'open';
      const updates: any = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'closed') {
        updates.closed_at = new Date().toISOString();
        updates.closed_by = userProfileId;
      } else {
        updates.closed_at = null;
        updates.closed_by = null;
      }

      const { error } = await supabase
        .from('verge_conditions')
        .update(updates)
        .eq('id', report.id);

      if (error) throw error;

      await fetchReports();
      if (selectedReport?.id === report.id) {
        setSelectedReport({ ...report, ...updates });
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      alert('Failed to update report status. Please try again.');
    }
  };

  const openReports = reports.filter(r => r.status !== 'closed');
  const closedReports = reports.filter(r => r.status === 'closed');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Authority Dashboard</h1>
                <p className="text-gray-600">{localAuthority}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">{reports.length}</div>
              <div className="text-sm text-gray-600">Total Reports</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-700">{openReports.length}</div>
              <div className="text-sm text-gray-600">Open Reports</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-700">{closedReports.length}</div>
              <div className="text-sm text-gray-600">Closed Reports</div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === 'all'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Reports
              </button>
              <button
                onClick={() => setFilterStatus('open')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === 'open'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Open Only
              </button>
              <button
                onClick={() => setFilterStatus('closed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === 'closed'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Closed Only
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'list'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <List className="w-4 h-4" />
                List View
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'map'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <MapIcon className="w-4 h-4" />
                Map View
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {viewMode === 'list' ? (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Reports ({reports.length})
                </h2>
                {reports.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                    No reports found for this filter.
                  </div>
                ) : (
                  reports.map(report => (
                    <div
                      key={report.id}
                      onClick={() => setSelectedReport(report)}
                      className={`bg-white rounded-lg shadow-lg p-4 cursor-pointer transition-all ${
                        selectedReport?.id === report.id
                          ? 'ring-2 ring-blue-500'
                          : 'hover:shadow-xl'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`${gradeColors[report.grade]} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                              Grade {report.grade}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              report.status === 'closed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {report.status === 'closed' ? 'Closed' : 'Open'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <MapPin className="w-4 h-4" />
                            <span className="font-medium">{report.street_name || 'Unknown location'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            <span>{formatRelativeTime(report.timestamp)}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <div className="flex items-center gap-1 text-blue-600">
                              <MessageSquare className="w-4 h-4" />
                              <span>{report.comment_count || 0} comments</span>
                            </div>
                          </div>
                        </div>
                        {report.photo_url && (
                          <img
                            src={report.photo_url}
                            alt="Report"
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: '600px' }}>
                {reports.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No reports to display on map
                  </div>
                ) : (
                  <MapContainer
                    center={[reports[0].latitude, reports[0].longitude]}
                    zoom={10}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MarkerClusterGroup>
                      {reports.map(report => (
                        <Marker
                          key={report.id}
                          position={[report.latitude, report.longitude]}
                          eventHandlers={{
                            click: () => setSelectedReport(report)
                          }}
                        >
                          <Popup>
                            <div className="p-2">
                              <div className={`${gradeColors[report.grade]} text-white px-2 py-1 rounded text-xs font-medium inline-block mb-2`}>
                                Grade {report.grade}
                              </div>
                              <div className="font-medium text-sm">{report.street_name || 'Unknown location'}</div>
                              <div className="text-xs text-gray-500 mt-1">{formatRelativeTime(report.timestamp)}</div>
                              <div className="text-xs text-gray-600 mt-1">
                                Status: {report.status === 'closed' ? 'Closed' : 'Open'}
                              </div>
                              {report.photo_url && (
                                <img
                                  src={report.photo_url}
                                  alt="Report"
                                  className="w-full mt-2 rounded"
                                />
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  </MapContainer>
                )}
              </div>
            )}

            <div className="sticky top-4">
              {selectedReport ? (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Report Details</h2>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Grade</label>
                      <div className={`${gradeColors[selectedReport.grade]} text-white px-4 py-2 rounded-lg inline-block mt-1`}>
                        Grade {selectedReport.grade}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Status</label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-4 py-2 rounded-lg font-medium ${
                          selectedReport.status === 'closed'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {selectedReport.status === 'closed' ? 'Closed' : 'Open'}
                        </span>
                        <button
                          onClick={() => handleToggleStatus(selectedReport)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedReport.status === 'closed'
                              ? 'bg-orange-500 hover:bg-orange-600 text-white'
                              : 'bg-green-500 hover:bg-green-600 text-white'
                          }`}
                        >
                          {selectedReport.status === 'closed' ? (
                            <>
                              <XCircle className="w-4 h-4 inline mr-1" />
                              Reopen
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              Close
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Location</label>
                      <p className="text-gray-800">{selectedReport.street_name || 'Unknown location'}</p>
                      <p className="text-sm text-gray-500">
                        {selectedReport.latitude.toFixed(6)}, {selectedReport.longitude.toFixed(6)}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Reported</label>
                      <p className="text-gray-800">{formatRelativeTime(selectedReport.timestamp)}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedReport.timestamp).toLocaleString()}
                      </p>
                    </div>

                    {selectedReport.photo_url && (
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-2">Photo</label>
                        <img
                          src={selectedReport.photo_url}
                          alt="Report"
                          className="w-full rounded-lg"
                        />
                      </div>
                    )}

                    {(selectedReport.report_sent_at || selectedReport.chaser1_sent_at || selectedReport.chaser2_sent_at) && (
                      <div className="border-t pt-4">
                        <label className="text-sm font-medium text-gray-600 block mb-2">Email Tracking</label>
                        <div className="space-y-1 text-sm">
                          {selectedReport.report_sent_at && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <AlertCircle className="w-4 h-4" />
                              Initial report sent: {new Date(selectedReport.report_sent_at).toLocaleString()}
                            </div>
                          )}
                          {selectedReport.chaser1_sent_at && (
                            <div className="flex items-center gap-2 text-orange-600">
                              <AlertCircle className="w-4 h-4" />
                              Chaser #1 sent: {new Date(selectedReport.chaser1_sent_at).toLocaleString()}
                            </div>
                          )}
                          {selectedReport.chaser2_sent_at && (
                            <div className="flex items-center gap-2 text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              Chaser #2 sent: {new Date(selectedReport.chaser2_sent_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">
                      Comments ({comments.length})
                    </h3>

                    <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                      {comments.length === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-4">
                          No comments yet. Be the first to comment!
                        </p>
                      ) : (
                        comments.map(comment => (
                          <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-700">
                                {comment.user_email || 'Unknown user'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatRelativeTime(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-gray-800 text-sm">{comment.comment}</p>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        placeholder="Add a comment..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={submitting}
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || submitting}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center text-gray-500">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Select a report to view details and add comments</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
